import { apiFetch } from './http';

/**
 * The shortest password the platform accepts
 * (`AccountService.MIN_PASSWORD_LENGTH`). The forms use it so a field can
 * refuse before the request goes out; the platform checks it regardless,
 * because a client is never the one that decides.
 */
export const MIN_PASSWORD_LENGTH = 10;

export type Role =
  | 'VIEWER'
  | 'PLAYER'
  | 'EDITOR'
  | 'COMMANDER'
  | 'CURATOR'
  | 'SERVER_MANAGER'
  | 'PANEL_ACCESS'
  | 'TESTER'
  | 'ADMIN';

export type IdentityProvider = 'STEAM' | 'DISCORD' | 'PASSWORD';

export interface Me {
  authenticated: boolean;
  id: number | null;
  handle: string | null;
  displayName: string | null;
  roles: Role[];
  identities: IdentityProvider[];
  /** True only while no account exists: the first registration bootstraps the admin. */
  registrationOpen: boolean;
  /** False only for a signed-in account that has not answered or skipped the first sign-in's questions. */
  onboarded?: boolean;
  /** The provider a sign-in just verified from a proof the browser carried (Steam or Discord bounced here minutes ago). */
  linkedNow?: 'STEAM' | 'DISCORD' | null;
  /** This account's Steam picture; absent while the platform has not fetched it, or when there is no Steam identity. */
  avatarUrl?: string | null;
  /** The tests page has something for this account: always for an admin, for anybody else once they took a card. */
  tests?: boolean;
  /** How many web test cards the account took and has not finished: the shell asks for them only when there is one. */
  webCards?: number;
}

export interface IdentityDetail {
  provider: 'STEAM' | 'DISCORD';
  subject: string;
  /** Confirmed by the provider (Steam's sign-in, Discord's); a false one is a typed claim. */
  verified: boolean;
  /** The name the game last saw this Steam id play under, when it has. */
  playerName: string | null;
  /** The name the provider knows the identity by (a Discord username), when it has one. */
  label?: string | null;
}

export const ANONYMOUS: Me = {
  authenticated: false,
  id: null,
  handle: null,
  displayName: null,
  roles: [],
  identities: [],
  registrationOpen: false,
};

export function fetchMe(): Promise<Me> {
  return apiFetch<Me>('/api/auth/me');
}

export function login(handle: string, password: string, remember: boolean): Promise<Me> {
  return apiFetch<Me>('/api/auth/login', { method: 'POST', body: { handle, password, remember } });
}

export function register(input: {
  handle: string;
  displayName: string;
  password: string;
  inviteCode: string;
}): Promise<Me> {
  return apiFetch<Me>('/api/auth/register', { method: 'POST', body: input });
}

/** Where the browser goes to sign in with Steam, or to link it to the current account. */
export function steamStartUrl(options: { link?: boolean; next?: string } = {}): string {
  const params = new URLSearchParams();
  if (options.link) params.set('link', 'true');
  if (options.next) params.set('next', options.next);
  const query = params.toString();
  return `/api/auth/steam/start${query ? `?${query}` : ''}`;
}

/**
 * Opens the account of someone a provider confirmed minutes ago whom the group
 * already knows (the sign-in page's `?steam=new` or `?discord=new`): somebody
 * the game has seen play, or a member of the group's Discord server. Who they
 * are is the proof the platform keeps in this browser's session, never
 * anything sent from here; the body is only what they chose for the account,
 * and no invitation code is asked. An empty password means "none for now".
 */
export function joinWithProvider(
  provider: 'STEAM' | 'DISCORD',
  input: { handle: string; displayName: string; password: string },
): Promise<{ handle: string; displayName: string }> {
  return apiFetch<{ handle: string; displayName: string }>(
    `/api/auth/${provider === 'STEAM' ? 'steam' : 'discord'}/join`,
    { method: 'POST', body: input },
  );
}

/**
 * Whether an invitation code would open a registration right now. Asking
 * spends nothing, and the platform says yes or no and nothing more; it counts
 * wrong codes per address and then refuses for a while (`throttled`).
 */
export function checkInvitation(code: string): Promise<{ valid: boolean }> {
  return apiFetch<{ valid: boolean }>('/api/auth/invitation', { method: 'POST', body: { code } });
}

export function unlinkSteam(): Promise<{ identities: IdentityProvider[] }> {
  return apiFetch<{ identities: IdentityProvider[] }>('/api/auth/steam', { method: 'DELETE' });
}

/** Where the browser goes to sign in with Discord, or to link it to the current account. */
export function discordStartUrl(options: { link?: boolean; next?: string } = {}): string {
  const params = new URLSearchParams();
  if (options.link) params.set('link', 'true');
  if (options.next) params.set('next', options.next);
  const query = params.toString();
  return `/api/auth/discord/start${query ? `?${query}` : ''}`;
}

export function unlinkDiscord(): Promise<{ identities: IdentityProvider[] }> {
  return apiFetch<{ identities: IdentityProvider[] }>('/api/auth/discord', { method: 'DELETE' });
}

export function logout(): Promise<void> {
  return apiFetch<void>('/api/auth/logout', { method: 'POST' });
}

/** The account changes its own password; an account without one (Steam or Discord only) sets it with no current password. */
export function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  return apiFetch<void>('/api/auth/password', {
    method: 'POST',
    body: { currentPassword, newPassword },
  });
}

/** One signed-in browser of the account (study 6.4). */
export interface DeviceView {
  id: string;
  userAgent: string;
  address: string;
  signedInAt: string;
  lastSeenAt: string;
  current: boolean;
}

export const fetchDevices = () => apiFetch<DeviceView[]>('/api/auth/devices');
export const revokeDevice = (id: string) =>
  apiFetch<void>(`/api/auth/devices/${encodeURIComponent(id)}`, { method: 'DELETE' });
